import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const BlogCard = ({ post }) => (
  <Link
    to={`/blog/${post.slug}`}
    className="group bg-white rounded-2xl shadow-md hover:shadow-xl transition-shadow overflow-hidden border border-gray-100 flex flex-col"
    data-testid={`blog-card-${post.slug}`}
  >
    <div className="aspect-video w-full overflow-hidden bg-[#0B1F4B]/5">
      {post.cover_image ? (
        <img src={post.cover_image} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-4xl font-black text-[#0B1F4B]/20">CDA</div>
      )}
    </div>
    <div className="p-5 flex-1 flex flex-col">
      <Badge className="w-fit bg-amber-100 text-amber-800 border-amber-300 mb-2">{post.category}</Badge>
      <h3 className="font-bold text-lg text-[#0B1F4B] group-hover:text-red-700 transition-colors leading-snug">{post.title}</h3>
      {post.excerpt && <p className="text-sm text-gray-600 mt-2 line-clamp-3 flex-1">{post.excerpt}</p>}
      <p className="text-xs text-gray-400 mt-3 flex items-center gap-1">
        <Calendar className="w-3 h-3" />
        {post.published_at ? new Date(post.published_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : ''}
      </p>
    </div>
  </Link>
);

export default function Blog() {
  const [data, setData] = useState({ posts: [], pages: 1, categories: [] });
  const [category, setCategory] = useState('');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    document.title = 'Blog | Cricket Drive Academy';
    let cancel = false;
    (async () => {
      setLoading(true);
      setLoadError(false);
      try {
        const p = new URLSearchParams({ page: String(page), limit: '9' });
        if (category) p.set('category', category);
        const r = await fetch(`${API_URL}/api/blog?${p}`);
        if (!r.ok) throw new Error('bad response');
        const d = await r.json();
        if (!cancel) setData(d);
      } catch (_e) {
        if (!cancel) setLoadError(true);
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [category, page]);

  return (
    <div className="min-h-screen bg-gray-50 pt-24 pb-16">
      <div className="max-w-6xl mx-auto px-6">
        <div className="mb-8">
          <h1 className="text-4xl sm:text-5xl font-black text-[#0B1F4B]">CDA <span className="text-red-700">Blog</span></h1>
          <p className="text-gray-600 mt-2">Program updates, coaching insights, match recaps and more from Cricket Drive Academy.</p>
        </div>

        {/* Category filter tabs */}
        <div className="flex flex-wrap gap-2 mb-8" data-testid="blog-category-tabs">
          <button
            onClick={() => { setCategory(''); setPage(1); }}
            className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-colors ${!category ? 'bg-[#0B1F4B] text-white border-[#0B1F4B]' : 'bg-white text-gray-700 border-gray-200 hover:border-[#0B1F4B]'}`}
            data-testid="blog-cat-all"
          >
            All
          </button>
          {(data.categories || []).map((c) => (
            <button
              key={c}
              onClick={() => { setCategory(c); setPage(1); }}
              className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-colors ${category === c ? 'bg-[#0B1F4B] text-white border-[#0B1F4B]' : 'bg-white text-gray-700 border-gray-200 hover:border-[#0B1F4B]'}`}
            >
              {c}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="py-20 text-center text-gray-400">Loading posts…</div>
        ) : loadError ? (
          <div className="py-20 text-center text-gray-500" data-testid="blog-error">Couldn't load posts right now — please try again shortly.</div>
        ) : data.posts.length === 0 ? (
          <div className="py-20 text-center text-gray-500" data-testid="blog-empty">No posts published yet — check back soon!</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="blog-grid">
            {data.posts.map((p) => <BlogCard key={p.post_id} post={p} />)}
          </div>
        )}

        {data.pages > 1 && (
          <div className="flex items-center justify-center gap-3 mt-10">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="blog-prev">
              <ChevronLeft className="w-4 h-4" /> Prev
            </Button>
            <span className="text-sm text-gray-600">Page {page} of {data.pages}</span>
            <Button variant="outline" size="sm" disabled={page >= data.pages} onClick={() => setPage(page + 1)} data-testid="blog-next">
              Next <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
